import * as React from 'react';
import { highlightTerm } from './highlight-term';
import { translate } from '@/lib/i18n/runtime';

/**
 * Normalize a URL written in a message without ever accepting executable
 * schemes. `www.` is intentionally supported because customers often paste
 * links without the protocol.
 */
export function normalizeMessageUrl(raw: string): string | null {
  const candidate = /^www\./i.test(raw) ? `https://${raw}` : raw;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/** Open message links only after an explicit user confirmation. */
export function confirmAndOpenMessageUrl(raw: string): boolean {
  const url = normalizeMessageUrl(raw);
  if (!url || typeof window === 'undefined') return false;
  if (!window.confirm(translate('externalLinkConfirm'))) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

interface RenderMessageTextOptions {
  /** Search highlighting wins over @mention styling, but links stay active. */
  highlight?: string;
  mentions?: boolean;
}

const MESSAGE_URL_RE = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

function splitTrailingPunctuation(value: string): { url: string; suffix: string } {
  let url = value;
  let suffix = '';
  while (/[.,!?;:]$/.test(url)) {
    suffix = url.slice(-1) + suffix;
    url = url.slice(0, -1);
  }
  // A closing bracket belongs to prose when there are more closers than
  // openers in the matched token: `(https://example.com)` should not navigate
  // to a URL ending in `)`, while `/docs_(v2)` remains intact.
  const pairs: Array<[string, string]> = [['(', ')'], ['[', ']'], ['{', '}']];
  for (const [open, close] of pairs) {
    while (url.endsWith(close)) {
      const opens = url.split(open).length - 1;
      const closes = url.split(close).length - 1;
      if (closes <= opens) break;
      suffix = close + suffix;
      url = url.slice(0, -1);
    }
  }
  return { url, suffix };
}

function renderPlainSegment(
  text: string,
  keyPrefix: string,
  options: RenderMessageTextOptions,
): React.ReactNode {
  if (!text) return null;
  if (options.highlight?.trim()) {
    return <React.Fragment key={keyPrefix}>{highlightTerm(text, options.highlight)}</React.Fragment>;
  }
  if (options.mentions === false) return <React.Fragment key={keyPrefix}>{text}</React.Fragment>;

  const matches = Array.from(text.matchAll(/@\w+/g));
  if (matches.length === 0) return <React.Fragment key={keyPrefix}>{text}</React.Fragment>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const match of matches) {
    const idx = match.index ?? 0;
    if (idx > cursor) {
      parts.push(
        <React.Fragment key={`${keyPrefix}-text-${key++}`}>
          {text.slice(cursor, idx)}
        </React.Fragment>,
      );
    }
    parts.push(
      <span
        key={`${keyPrefix}-mention-${key++}`}
        style={{
          display: 'inline',
          fontWeight: 700,
          color: 'var(--blaze-orange)',
          background: 'rgba(255, 90, 31, 0.10)',
          padding: '0 4px',
          borderRadius: 4,
        }}
      >
        {match[0]}
      </span>,
    );
    cursor = idx + match[0].length;
  }
  if (cursor < text.length) {
    parts.push(
      <React.Fragment key={`${keyPrefix}-text-${key}`}>{text.slice(cursor)}</React.Fragment>,
    );
  }
  return parts;
}

/**
 * Renders safe message text with styled `@mentions` and confirmed external
 * links. Text still flows through React nodes (never interpreted as HTML), so
 * linkification cannot turn message content into executable markup.
 *
 * Pattern: `@` followed by word chars. The picker today inserts a single
 * `@FirstName` token, so we keep the regex narrow on purpose. If multi-word
 * mentions become a need we'll switch to a marker scheme like `<@uuid>`.
 *
 * No HTML interpretation — segments go through React's normal text nodes
 * so injection is impossible by construction.
 */
export function renderTextWithMentions(
  text: string | undefined | null,
  options: RenderMessageTextOptions = {},
): React.ReactNode {
  if (!text) return text;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const match of text.matchAll(MESSAGE_URL_RE)) {
    const idx = match.index ?? 0;
    if (idx > cursor) {
      parts.push(renderPlainSegment(text.slice(cursor, idx), `plain-${key++}`, options));
    }
    const { url, suffix } = splitTrailingPunctuation(match[0]);
    const normalized = normalizeMessageUrl(url);
    if (normalized) {
      parts.push(
        <a
          key={`link-${key++}`}
          href={normalized}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-inherit underline decoration-current/50 underline-offset-2 hover:decoration-current"
          onClick={(event) => {
            event.preventDefault();
            confirmAndOpenMessageUrl(url);
          }}
        >
          {url}
        </a>,
      );
    } else {
      parts.push(renderPlainSegment(url, `plain-${key++}`, options));
    }
    if (suffix) {
      parts.push(
        <React.Fragment key={`suffix-${key++}`}>{suffix}</React.Fragment>,
      );
    }
    cursor = idx + match[0].length;
  }
  if (cursor < text.length) {
    parts.push(renderPlainSegment(text.slice(cursor), `plain-${key}`, options));
  }
  if (parts.length === 0) {
    return renderPlainSegment(text, 'plain-only', options);
  }
  return parts;
}
